/**
 * Workaround Angular Module so we can have downgraded Angular injectables in the embed
 */

import { HttpClientModule } from '@angular/common/http';
import { ApplicationRef, ErrorHandler, NgModule } from '@angular/core';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { UpgradeModule } from '@angular/upgrade/static';
import { CapraIconsRegistry } from '@mode/capra';
import { ReportSandboxErrorHandler, ReportSandboxErrorService } from '@mode/reports/sandbox/domain-viewer';
import { ErrorReporter } from '@mode/shared/contract-common';
import { SharedDataAccessFlamingoModule } from '@mode/shared/data-access-flamingo';
import { SharedDataAccessWebappModule } from '@mode/shared/data-access-webapp';
import { SharedUtilErrorHandlingModule } from '@mode/shared/util-error-handling';
import { SharedUtilFeatureFlagsModule } from '@mode/shared/util-feature-flags';
import { EMBED_ICONS_ANALYST_STUDIO, EMBED_ICONS_MODE } from '@mode/shared/util-js';
import { ObservabilityService } from '@mode/shared/util-observability';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { AppComponent } from './app.component';
import { EmbedReportBootstrapService } from './bootstrap';
import { EMBED_VIEWER_STORE } from './domain/legacy-embed-viewer-store.service';
import { EmptyObservabilityService } from './services/empty-observability-service';
import { SharedUtilLoggingModule } from '@mode/shared/util-logging';
import {
  SharedUtilThoughtspotIntegratedModule,
  ThoughtspotIntegrationService,
} from '@mode/shared/util-thoughtspot-integrated';

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    HttpClientModule,
    BrowserAnimationsModule,
    UpgradeModule,
    SharedUtilErrorHandlingModule,
    SharedUtilFeatureFlagsModule,
    SharedUtilThoughtspotIntegratedModule,
    SharedUtilLoggingModule,
    SharedDataAccessWebappModule,
    SharedDataAccessFlamingoModule,
    StoreModule.forRoot({}), // Have to start up the root because we're outside the main app initialization
    EffectsModule.forRoot(),
    ...EMBED_VIEWER_STORE,
  ],
  providers: [
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
    { provide: Window, useValue: window },
    { provide: ObservabilityService, useValue: EmptyObservabilityService },
    { provide: ErrorReporter, useClass: ReportSandboxErrorService },
    { provide: ErrorHandler, useClass: ReportSandboxErrorHandler },
  ],
})
export class EmbedReportModule {
  constructor(
    private upgrade: UpgradeModule,
    private legacy: EmbedReportBootstrapService,
    private capraIconsRegistry: CapraIconsRegistry,
    private thoughtspotIntegratedService: ThoughtspotIntegrationService
  ) {
    if (this.thoughtspotIntegratedService.currOrgIntegrated()) {
      this.capraIconsRegistry.registerIcons(EMBED_ICONS_ANALYST_STUDIO);
    } else {
      this.capraIconsRegistry.registerIcons(EMBED_ICONS_MODE);
    }
  }

  async ngDoBootstrap(appRef: ApplicationRef) {
    appRef.bootstrap(AppComponent);
    await this.legacy.bootstrap(this.upgrade);
  }
}
